import React, { useState, useMemo } from "react";
import { connect } from "react-redux";
import { Layout, Select } from "antd";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import SidebarLogo from "./SidebarLogo";
import MenuContent from "./MenuContent";
import SearchInput from "./NavSearch/SearchInput";
import NavProfile from "./NavProfile";
import ThemeToggle from "./ThemeToggle";
import { toggleCollapsedNav, onMobileNavToggle } from "redux/actions/Theme";
import {
	NAV_TYPE_TOP,
	SIDE_NAV_COLLAPSED_WIDTH,
	SIDE_NAV_WIDTH,
	TEMPLATE,
} from "constants/ThemeConstant";
import utils from "utils";
import MenuService from "services/MenuService";
import { usePageTitle } from "contexts/PageTitleContext";
import IntlMessage from "components/util-components/IntlMessage";
import "./header-nav-dimo.css";
import { THEME_KEYS } from "utils/themeDisplay";

const DEFAULT_TENANT_SCHEMA =
	process.env.REACT_APP_DEFAULT_TENANT_SCHEMA || "tenant_base";

const { Header } = Layout;

export const HeaderNav = (props) => {
	const {
		navCollapsed,
		mobileNav,
		navType,
		toggleCollapsedNav: toggleCollapsedNavAction,
		onMobileNavToggle: onMobileNavToggleAction,
		isMobile,
		currentTheme,
		user,
		routeInfo,
	} = props;
	const [switchingTenant, setSwitchingTenant] = useState(false);
	const { title: pageTitle, extra: pageExtra } = usePageTitle();
	const isNavSide = navType !== NAV_TYPE_TOP;
	const isSideNavWithHeaderBar = isNavSide && !isMobile;
	const isDarkTheme =
		currentTheme === THEME_KEYS.DARK ||
		currentTheme === THEME_KEYS.QUESTOR;

	const onToggle = () => {
		if (!isMobile) {
			toggleCollapsedNavAction(!navCollapsed);
		} else {
			onMobileNavToggleAction(!mobileNav);
		}
	};

	const navBgColor = isDarkTheme
		? TEMPLATE.HEADER_BG_DEFAULT_COLOR_DARK
		: TEMPLATE.HEADER_BG_DEFAULT_COLOR_LIGHT;

	const navMode = useMemo(
		() => utils.getColorContrast(isDarkTheme ? "#000000" : "#ffffff"),
		[isDarkTheme],
	);

	const displayTitle =
		pageTitle != null && pageTitle !== ""
			? pageTitle
			: routeInfo?.title
				? routeInfo.title
				: null;
	const titleLabel = (() => {
		if (!displayTitle) return null;
		if (typeof displayTitle === "string")
			return <IntlMessage id={displayTitle} />;
		if (React.isValidElement(displayTitle)) return displayTitle;
		if (displayTitle?.id != null)
			return (
				<IntlMessage
					id={displayTitle.id}
					defaultMessage={displayTitle.defaultMessage}
				/>
			);
		return null;
	})();

	const tenants = user?.tenants || [];
	const selectedTenant = user?.selectedTenant;
	const showTenantSelector = tenants.length > 0;

	const handleTenantChange = async (value) => {
		if (!value || value === selectedTenant) {
			return;
		}
		setSwitchingTenant(true);
		try {
			await MenuService.applyTenantAndLoadMenu(value);
			window.location.reload();
		} finally {
			setSwitchingTenant(false);
		}
	};
	const normalizedTenant = (
		selectedTenant ||
		tenants[0]?.nome_schema ||
		DEFAULT_TENANT_SCHEMA
	)?.toLowerCase();
	const isTenantBaseSelected = normalizedTenant === DEFAULT_TENANT_SCHEMA;

	const showDesktopProfile = !(isNavSide && !isMobile);

	return (
		<Header
			className={`app-header header-nav-dimo ${navMode} ${
				isDarkTheme ? "header-nav-dimo--dark" : "header-nav-dimo--light"
			}`}
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				right: 0,
				zIndex: 1000,
				height: TEMPLATE.HEADER_HEIGHT,
				lineHeight: `${TEMPLATE.HEADER_HEIGHT}px`,
				padding: "0 16px 0 0",
				backgroundColor: navBgColor,
				width: "100%",
				boxShadow: isDarkTheme
					? "0 1px 8px -1px rgb(0 0 0 / 75%)"
					: "0 1px 4px -1px rgb(0 0 0 / 15%)",
			}}
		>
			<div
				className="app-header-wrapper d-flex align-items-stretch"
				style={{ width: "100%", minHeight: TEMPLATE.HEADER_HEIGHT }}
			>
				{isSideNavWithHeaderBar ? (
					<div
						className={`header-nav-logo-wrap ${
							isDarkTheme ? "header-nav-logo-wrap--dark" : ""
						}`}
						style={{
							width: navCollapsed
								? SIDE_NAV_COLLAPSED_WIDTH
								: SIDE_NAV_WIDTH,
							flexShrink: 0,
						}}
					>
						<SidebarLogo collapsed={navCollapsed} placement="header" />
					</div>
				) : null}
				<div
					className="nav flex-grow-1 d-flex align-items-center"
					style={{
						minWidth: 0,
						backgroundColor: isTenantBaseSelected ? "#fa8c16" : "transparent",
						transition: "background-color 0.3s ease",
					}}
				>
					<div className="header-nav-row">
						<div className="header-nav-left">
							{isNavSide || isMobile ? (
								<button
									type="button"
									className={`header-nav-toggle-btn ${
										isDarkTheme
											? "header-nav-toggle-btn--dark"
											: "header-nav-toggle-btn--light"
									}`}
									onClick={onToggle}
									aria-label={
										navCollapsed || isMobile
											? "Expandir menu"
											: "Recolher menu"
									}
								>
									{navCollapsed || isMobile ? (
										<MenuUnfoldOutlined />
									) : (
										<MenuFoldOutlined />
									)}
								</button>
							) : null}
							{!isSideNavWithHeaderBar ? (
								<div
									className="header-nav-search"
									style={{
										marginLeft: 8,
										marginRight: 12,
										minWidth: isMobile ? 140 : 220,
										maxWidth: isMobile ? 180 : 320,
										flex: isMobile ? "1 1 140px" : "0 1 280px",
									}}
								>
									<SearchInput
										mode={isDarkTheme ? "dark" : "light"}
										currentTheme={isDarkTheme ? "dark" : "light"}
										variant="header"
										isMobile={isMobile}
									/>
								</div>
							) : null}
							{isSideNavWithHeaderBar && titleLabel && (
								<h1 className="app-header-page-title mb-0 font-weight-semibold">
									{titleLabel}
								</h1>
							)}
						</div>
						{navType === NAV_TYPE_TOP && !isMobile ? (
							<div className="nav-center flex-grow-1 d-flex justify-content-center">
								<MenuContent
									type={NAV_TYPE_TOP}
									topNavColor="transparent"
									localization={true}
								/>
							</div>
						) : null}
						<div className="header-nav-spacer" />
						<div className="nav-right header-nav-tools d-flex align-items-center">
							{isSideNavWithHeaderBar && pageExtra && (
								<div
									className="app-header-page-extra"
									style={{ marginRight: 16 }}
								>
									{pageExtra}
								</div>
							)}
							{showTenantSelector && (
								<div style={{ marginRight: 16 }}>
									<Select
										size="small"
										value={
											selectedTenant ||
											tenants[0]?.nome_schema ||
											undefined
										}
										style={{ minWidth: 200 }}
										loading={switchingTenant}
										disabled={switchingTenant || tenants.length < 2}
										onChange={handleTenantChange}
									>
										{tenants.map((tenant) => (
											<Select.Option
												key={tenant.nome_schema}
												value={tenant.nome_schema}
											>
												{tenant.nome} ({tenant.nome_schema})
											</Select.Option>
										))}
									</Select>
								</div>
							)}
							<ThemeToggle compact />
							{showDesktopProfile ? <NavProfile /> : null}
						</div>
					</div>
				</div>
			</div>
		</Header>
	);
};

const mapStateToProps = ({ theme, user }) => {
	const {
		navCollapsed,
		navType,
		mobileNav,
		currentTheme,
		direction,
	} = theme;
	return {
		navCollapsed,
		navType,
		mobileNav,
		currentTheme,
		direction,
		user,
	};
};

export default connect(mapStateToProps, {
	toggleCollapsedNav,
	onMobileNavToggle,
})(HeaderNav);
